import Loader from '@components/Loader/Loader';
import useMountedRef from '@hooks/use-mounted-ref';
import { getImageDimensions, resizeImage } from '@modules/image-helpers';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import styles from './ImageSelector.module.scss';

interface ImageSize {
  width: number;
  height: number;
}

interface ImageSelectorProps {
  onSelect: (imageReference: string) => void;
  acceptMultiple?: boolean;
  maxSize?: number;
  maxDimension?: number;
  minDimension?: number;
  sizeRecommendation?: ImageSize;
  disabled?: boolean;
}

const ImageSelector: React.FC<React.PropsWithChildren<ImageSelectorProps>> = ({
  onSelect,
  acceptMultiple = false,
  maxSize = 10000000,
  maxDimension,
  minDimension,
  sizeRecommendation,
  disabled = false,
}) => {
  const [errorNotifications, setErrorNotifications] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useMountedRef();

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        const errorsToReport = fileRejections.reduce((errorsToReport, { errors }) => {
          errors.forEach(({ code }) => {
            if (code === 'too-many-files') {
              errorsToReport.add('Too many files provided. Please supply a single file.');
            } else if (code === 'file-invalid-type') {
              errorsToReport.add('Only JPEGs and PNGs are accepted.');
            } else if (code === 'file-too-large') {
              errorsToReport.add(`This file is too large. It must be less than ${maxSize / 1000000}MB.`);
            } else if (code === 'file-too-small') {
              // Ignore
            }
          });

          return errorsToReport;
        }, new Set<string>());

        setErrorNotifications([...errorsToReport]);
      } else {
        const errorsToReport = new Set<string>();
        if (minDimension) {
          const dimensions = await Promise.all(acceptedFiles.map(getImageDimensions));
          for (const d of dimensions) {
            if (d[0] < minDimension || d[1] < minDimension) {
              errorsToReport.add(
                `Your image is too small, images must be larger than ${minDimension}px in both dimensions.`,
              );
            }
          }
          if (errorsToReport.size > 0) {
            setErrorNotifications([...new Set(errorsToReport)]);
          }
        }
        if (errorsToReport.size > 0) {
          return;
        } else if (maxDimension) {
          setLoading(true);

          const resizedImageBlobs = await Promise.all(
            acceptedFiles.map((file) => resizeImage(file, maxDimension, maxDimension)),
          );

          resizedImageBlobs.forEach(async (resizedImageBlob) => {
            const url = URL.createObjectURL(resizedImageBlob);

            onSelect(url);
            if (isMountedRef.current) setLoading(false);
          });
        } else {
          acceptedFiles.forEach((file) => {
            const url = URL.createObjectURL(file);

            onSelect(url);
          });
        }
      }
    },
    [onSelect, maxSize, maxDimension, minDimension, isMountedRef],
  );

  function hideErrorNotification(error: string) {
    setErrorNotifications((errorNotifications) => {
      return errorNotifications.filter((notification) => notification !== error);
    });
  }

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg'],
      'image/jpg': ['.jpg'],
      'image/png': ['.png'],
    },
    multiple: acceptMultiple,
    maxSize,
    disabled,
  });

  const dropzoneContentsVariants = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  };

  return (
    <div className={styles['image-selector']}>
      <div
        className={styles['drop-zone']}
        {...getRootProps()}
        data-dragging={isDragActive}
        data-invalid-files={isDragReject}
      >
        <input {...getInputProps()} />
        {!loading && (
          <>
            <motion.div
              className={styles['drop-zone-icon']}
              initial="hidden"
              animate="visible"
              variants={dropzoneContentsVariants}
            >
              <svg width="0" height="0">
                {/* biome-ignore lint/correctness/useUniqueElementIds: historic */}
                <radialGradient id="dropzone-gradient" r="150%" cx="15%" cy="30%">
                  <stop stopColor="#97c8ed" offset="0" />
                  <stop stopColor="#1f87ff" offset="0.6" />
                </radialGradient>
              </svg>
            </motion.div>
            <motion.p
              className={styles['drop-zone-text']}
              initial="hidden"
              animate="visible"
              variants={dropzoneContentsVariants}
            >
              Drop your image here, or <span className={styles['browse-text']}>browse</span>
            </motion.p>
            {sizeRecommendation && (
              <motion.p
                className={styles['drop-zone-size-recommendation']}
                initial="hidden"
                animate="visible"
                variants={dropzoneContentsVariants}
              >
                {`${sizeRecommendation.width} x ${sizeRecommendation.height}px size recommended`}
              </motion.p>
            )}
            <motion.p
              className={styles['drop-zone-subtext']}
              initial="hidden"
              animate="visible"
              variants={dropzoneContentsVariants}
            >
              Supports JPG, JPEG, and PNG
            </motion.p>
          </>
        )}
        {loading && <Loader />}
        {errorNotifications.length > 0 && (
          <ul className={styles['error-notifications']}>
            <AnimatePresence>
              {errorNotifications.map((error, i) => (
                <motion.li
                  className={styles['error-notification']}
                  // biome-ignore lint/suspicious/noArrayIndexKey: historic
                  key={i}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <span className={styles['error-notification-text']}>{error}</span>

                  <svg
                    className={styles['error-notification-countdown']}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    onAnimationEnd={() => hideErrorNotification(error)}
                  >
                    <circle r="10" cx="12" cy="12"></circle>
                  </svg>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </div>
  );
};

export default ImageSelector;
